import React from 'react'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, useFormContext } from 'react-hook-form'

import { TextFieldMenu } from 'components'
import { QuestionnaireForm } from 'views/ThirdPartyManagements/components'

import helpers from 'helpers'
import constants from 'constants/index'

import useStyles from './styles'

const AutomationForm = ({
  onSubmit,
  nextAuditDate,
  quizTemplateIds,
  setQuizTemplateIds,
  auditConfig = null,
}) => {
  const classes = useStyles()
  const { control, errors, watch } = useFormContext()

  const automationEnabled = watch('active')

  const { ACTIVE_STATUS } = constants.companySuppliers

  const isActive = auditConfig?.status === ACTIVE_STATUS

  const { pastBlockProps } = helpers.functions.datePickerPastBlocker(
    auditConfig?.startDate,
  )

  return (
    <form
      id="automation-modal-form"
      onSubmit={onSubmit}
      className={classes.formContainer}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            className={clsx(classes.switchHighlight, {
              [classes.switchHighlightActive]: automationEnabled,
            })}
            control={
              <Controller
                as={<Switch color="primary" />}
                name="active"
                control={control}
              />
            }
            labelPlacement="start"
            label={
              <Box>
                <Typography variant="body1" className={classes.switchLabelText}>
                  Ativar automação
                </Typography>
                <Typography
                  variant="caption"
                  className={clsx(classes.switchStatusText, {
                    [classes.switchStatusTextActive]: automationEnabled,
                  })}
                >
                  {automationEnabled
                    ? 'Automação ativada — a auditoria será iniciada na data configurada.'
                    : 'Automação inativa — a auditoria não será iniciada. Ative para agendar.'}
                </Typography>
              </Box>
            }
          />
        </Grid>

        {!isActive && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body1">
                Ao ativar a automação, a data de início e a periodicidade
                ficarão bloqueadas para preservar a agenda de auditoria. Para
                ajustá-las, inative a automação primeiro.
              </Typography>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Controller
            as={
              <KeyboardDatePicker
                fullWidth
                format="DD/MM/yyyy"
                label="Data de início da auditoria *"
                helperText={errors?.startDate?.message}
                error={!!errors?.startDate}
                invalidDateMessage="Data inválida"
                disabled={isActive}
                {...pastBlockProps}
              />
            }
            defaultValue={null}
            control={control}
            name="startDate"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextFieldMenu
            label="Periodicidade *"
            controlNameValue="recurrence"
            controlNameType="recurrencePeriodType"
            formatter={helpers.formatters.onlyNumbers}
            disabled={isActive}
          />
        </Grid>

        {nextAuditDate && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body1">
                Próxima auditoria em{' '}
                {helpers.formatters.dates.ptBrFormatDate(nextAuditDate)}
              </Typography>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextFieldMenu
            label="Prazo *"
            controlNameValue="duration"
            controlNameType="durationPeriodType"
            formatter={helpers.formatters.onlyNumbers}
          />
        </Grid>

        <Grid item xs={12}>
          <QuestionnaireForm
            selectedQuizTemplates={quizTemplateIds}
            setSelectedQuizTemplates={setQuizTemplateIds}
          />
          {!!errors?.quizTemplateIds && (
            <FormHelperText error>
              {errors.quizTemplateIds.message}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

AutomationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  nextAuditDate: PropTypes.string,
  quizTemplateIds: PropTypes.array.isRequired,
  setQuizTemplateIds: PropTypes.func.isRequired,
  auditConfig: PropTypes.object,
}

export default AutomationForm
